import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Layout } from '@/components/Layout';
import { useBetting } from '@/hooks/useBetting';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/lib/i18n';
import { supabase } from '@/integrations/supabase/client';
import { Match, User, MatchStatus, BetStatus, UserTier, toMYT } from '@/lib/index';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BarChart2, TrendingUp, Plus, Minus, Users, Save, Check, X, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';


// ── Custom portal dropdown (avoids overflow-x clipping of native <select>) ────
interface PctDropdownProps {
  value: number;
  options: number[];
  onChange: (v: number) => void;
  disabled?: boolean;
}

function PctDropdown({ value, options, onChange, disabled }: PctDropdownProps) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const openDropdown = () => {
    if (disabled) return;
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current && !btnRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={openDropdown}
        disabled={disabled}
        className="flex items-center gap-1 text-[8pt] border rounded px-2 py-1 bg-background hover:bg-muted focus:outline-none focus:ring-1 focus:ring-primary min-w-[64px] justify-between disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span>{value}%</span>
        <ChevronDown className="h-3 w-3 text-muted-foreground" />
      </button>

      {open && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div
          style={{
            position: 'absolute',
            top: pos.top + 4,
            left: pos.left,
            minWidth: Math.max(pos.width, 80),
            zIndex: 9999,
          }}
          className="bg-popover border border-border rounded shadow-lg py-1 max-h-48 overflow-y-auto"
          onMouseDown={e => e.stopPropagation()}
        >
          {options.map(p => (
            <button
              key={p}
              type="button"
              onClick={() => { onChange(p); setOpen(false); }}
              className={cn(
                'w-full text-left px-3 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground transition-colors',
                p === value && 'bg-primary/10 font-semibold text-primary'
              )}
            >
              {p}%
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────

function getDescendantIds(userId: string, allUsers: User[]): string[] {
  const ids: string[] = [];
  for (const u of allUsers) {
    if (u.parentId === userId) {
      ids.push(u.id);
      ids.push(...getDescendantIds(u.id, allUsers));
    }
  }
  return ids;
}

function classifyScore(score: string): 'home' | 'draw' | 'away' | 'aos' {
  if (score.toUpperCase() === 'AOS') return 'aos';
  const parts = score.split('-');
  if (parts.length !== 2) return 'aos';
  const h = parseInt(parts[0], 10);
  const a = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(a)) return 'aos';
  if (h > a) return 'home';
  if (h === a) return 'draw';
  return 'away';
}

const CANONICAL_SCORE_ORDER = [
  '1-0','2-0','2-1','3-0','3-1','3-2','4-0','4-1','4-2','4-3',
  '0-0','1-1','2-2','3-3','4-4',
  'AOS',
  '0-1','0-2','1-2','0-3','1-3','2-3','0-4','1-4','2-4','3-4',
];

function sortScoreKeys(keys: string[]): string[] {
  const upper = keys.map(k => k.toUpperCase() === 'AOS' ? 'AOS' : k);
  return [...upper].sort((a, b) => {
    const ai = CANONICAL_SCORE_ORDER.indexOf(a);
    const bi = CANONICAL_SCORE_ORDER.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return 0;
  });
}

// Composite key: score + userId
function settingKey(score: string, userId: string) {
  return `${score}__${userId}`;
}

// ── types ─────────────────────────────────────────────────────────────────────

interface ScoreData {
  totalAfterRate: number;
  totalBetsPlaced: number;
  // Sum of rebates paid out to each individual bettor = Σ(bet amount × (1 - bettor ticketPrice))
  // Used for Company/Master-level Rebate column
  totalDownlineRebate: number;
  perUser: Record<string, { tickets: number; ticketPrice: number; afterRate: number; username: string }>;
}

/** Saved (persisted) fight setting for one score+user */
interface FightSetting {
  fightTicket: number;
  fightPct: number;
}

/** Draft edit state for one score+user */
interface DraftState {
  fightTicket: string;
  fightPct: number;
  confirming: boolean;
}

interface TPreMatch {
  odds: string;
  totalAfterRate: string;
  rebate: string;
  focus: string;
  possibleWinLoss: string;
  totalAfterCommission: string;
  username: string;
  tickets: string;
  ticketRate: string;
}

// ── CombinedScoreTable ────────────────────────────────────────────────────────

interface CombinedScoreTableProps {
  homeScores: string[];
  drawScores: string[];
  awayScores: string[];
  summaryByScore: Record<string, ScoreData>;
  scoreOdds: Record<string, number>;
  tPreMatch: TPreMatch;
  viewerTicketPrice: number;
  /** NIL = no simulation; any other string = the score being simulated as winning */
  simulatedScore: string;
  /** Company/Master viewers see total downline rebates paid out instead of own rebate rate */
  isCompany: boolean;
}

function CombinedScoreTable({
  homeScores, drawScores, awayScores,
  summaryByScore, scoreOdds, tPreMatch, viewerTicketPrice,
  simulatedScore, isCompany,
}: CombinedScoreTableProps) {
  const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const isSimulating = simulatedScore !== 'NIL';

  // All scores in order: home → draw → away
  const allScoresBase = [...homeScores, ...drawScores, ...awayScores];
  // NIL: show all scores. Non-NIL (score selected): only show rows with bets placed
  const allScores = isSimulating
    ? allScoresBase.filter(sc => (summaryByScore[sc]?.totalBetsPlaced ?? 0) > 0)
    : allScoresBase;

  // Category label for each score
  const categoryOf = (score: string): { label: string; cls: string } => {
    if (homeScores.includes(score)) return { label: 'Home', cls: 'text-blue-600' };
    if (drawScores.includes(score)) return { label: 'Draw', cls: 'text-yellow-600' };
    return { label: 'Away', cls: 'text-red-600' };
  };

  // rebateRate = 1 - ticketPrice  (e.g. ticketPrice=0.95 → rebateRate=0.05 = 5%)
  const rebateRate = 1 - viewerTicketPrice;

  // Per-row calculations
  // Rebate:
  //   Agent/Player: totalBetsPlaced × own rebateRate
  //   Company/Master: sum of rebates paid out to each downline bettor (totalDownlineRebate)
  const calcRebate = (data: ScoreData): number =>
    isCompany ? data.totalDownlineRebate : data.totalBetsPlaced * rebateRate;
  // Focus:
  //   NIL mode:     totalBetsPlaced × odd  (for every row)
  //   Non-NIL mode: totalBetsPlaced × odd  for the winning score only; 0 for all other scores
  const calcFocus = (sc: string, bp: number): number => {
    if (!isSimulating) return bp * (scoreOdds[sc] ?? 0);
    return sc === simulatedScore ? bp * (scoreOdds[sc] ?? 0) : 0;
  };

  // Grand subtotals across ALL displayed scores
  const subtotalBets   = allScores.reduce((s, sc) => s + (summaryByScore[sc]?.totalBetsPlaced ?? 0), 0);
  const subtotalRebate = allScores.reduce((s, sc) => {
    const d = summaryByScore[sc] ?? { totalBetsPlaced: 0, totalDownlineRebate: 0, totalAfterRate: 0, perUser: {} };
    return s + calcRebate(d);
  }, 0);
  const subtotalFocus  = allScores.reduce((s, sc) => s + calcFocus(sc, summaryByScore[sc]?.totalBetsPlaced ?? 0), 0);

  // Amount(RM) per score = totalBets - rebate; grand total
  const subtotalAmountRM = subtotalBets - subtotalRebate;
  // Amount Paid(RM) grand total = Σ(amountRM - focus) per row
  const subtotalAmountPaid = allScores.reduce((s, sc) => {
    const d = summaryByScore[sc] ?? { totalBetsPlaced: 0, totalDownlineRebate: 0, totalAfterRate: 0, perUser: {} };
    const bp = d.totalBetsPlaced;
    const rebate = calcRebate(d);
    const amountRM = bp - rebate;
    const focus = calcFocus(sc, bp);
    return s + (amountRM - focus);
  }, 0);
  // Per-row Possible Win/Loss = grandAmountRM + this row's Amount Paid
  // Footer = Σ all rows = subtotalAmountRM × nRows + subtotalAmountPaid
  const footerPossibleWinLoss = subtotalAmountRM * allScores.length + subtotalAmountPaid;

  return (
    <Card className="overflow-hidden">
      {allScores.length === 0 ? (
        <div className="px-4 py-4 text-sm text-muted-foreground text-center">—</div>
      ) : (
        <div className="overflow-x-auto text-[6pt] lg:text-base">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/20">
                <th className="text-left px-2 py-2 text-xs font-medium text-muted-foreground w-16">Score</th>
                <th className="text-left px-2 py-2 text-xs font-medium text-muted-foreground w-14">Type</th>
                <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground w-14">{tPreMatch.odds}</th>
                <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap">{tPreMatch.totalAfterRate}</th>
                <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap">{tPreMatch.rebate}</th>
                <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap">Amount (RM)</th>
                <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground">{tPreMatch.focus}</th>
                <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap">Amount Paid (RM)</th>
                <th className="text-right px-2 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap">{tPreMatch.possibleWinLoss}</th>
              </tr>
            </thead>
            <tbody>
              {allScores.map(score => {
                const data = summaryByScore[score] ?? { totalAfterRate: 0, totalBetsPlaced: 0, totalDownlineRebate: 0, perUser: {} };
                const bp      = data.totalBetsPlaced;
                const odd     = scoreOdds[score] ?? 0;
                const rebate  = calcRebate(data);
                const amountRM = bp - rebate;
                const focus   = calcFocus(score, bp);
                // Amount Paid (RM) = Amount(RM) - Focus
                const amountPaid = amountRM - focus;
                // Per-row Possible Win/Loss = grandAmountRM + this row's Amount Paid
                const possibleWinLoss = subtotalAmountRM + amountPaid;
                const { label, cls } = categoryOf(score);
                const isWinningScore = isSimulating && score === simulatedScore;

                return (
                  <tr key={score} className={cn(
                    'border-b transition-colors',
                    isWinningScore ? 'bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50' : 'hover:bg-muted/20'
                  )}>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <Badge
                        variant="outline"
                        className={cn('font-bold text-xs px-1.5 py-0', isWinningScore && 'border-amber-500 text-amber-700 dark:text-amber-400')}
                      >
                        {score}{isWinningScore && ' ★'}
                      </Badge>
                    </td>
                    <td className={cn('px-2 py-2 text-xs font-semibold', cls)}>{label}</td>
                    <td className="px-2 py-2 text-right text-xs">
                      <span className="font-semibold text-foreground">{odd.toFixed(2)}</span>
                    </td>
                    {/* Total Bets Placed */}
                    <td className="px-2 py-2 text-right text-xs font-semibold text-black tabular-nums whitespace-nowrap">
                      {fmt(bp)}
                    </td>
                    {/* Rebate */}
                    <td className="px-2 py-2 text-right text-xs font-semibold text-black tabular-nums whitespace-nowrap">
                      {fmt(rebate)}
                    </td>
                    {/* Amount (RM) = Total Bets - Rebate */}
                    <td className="px-2 py-2 text-right text-xs font-bold text-black tabular-nums whitespace-nowrap">
                      {fmt(amountRM)}
                    </td>
                    {/* Focus */}
                    <td className="px-2 py-2 text-right text-xs font-semibold text-black tabular-nums whitespace-nowrap">
                      {fmt(focus)}
                    </td>
                    {/* Amount Paid (RM) = Amount(RM) - Focus */}
                    <td className={cn('px-2 py-2 text-right text-xs font-bold tabular-nums whitespace-nowrap',
                      amountPaid < 0 ? 'text-red-600' : 'text-black')}>
                      {fmt(amountPaid)}
                    </td>
                    {/* Possible Win/Loss = subtotalBets + this row's Amount Paid */}
                    <td className={cn('px-2 py-2 text-right text-xs font-bold tabular-nums whitespace-nowrap',
                      possibleWinLoss < 0 ? 'text-red-600' : 'text-black')}>
                      {fmt(possibleWinLoss)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t bg-muted/30">
                <td className="px-2 py-2 text-xs text-muted-foreground font-medium" colSpan={3}>Total</td>
                <td className="px-2 py-2 text-right text-xs font-bold text-black tabular-nums">
                  {fmt(subtotalBets)}
                </td>
                <td className="px-2 py-2 text-right text-xs font-bold text-black tabular-nums">
                  {fmt(subtotalRebate)}
                </td>
                <td className="px-2 py-2 text-right text-xs font-bold text-black tabular-nums">
                  {fmt(subtotalAmountRM)}
                </td>
                <td className="px-2 py-2 text-right text-xs font-bold text-black tabular-nums">
                  {fmt(subtotalFocus)}
                </td>
                <td className={cn('px-2 py-2 text-right text-xs font-bold tabular-nums',
                  subtotalAmountPaid < 0 ? 'text-red-600' : 'text-black')}>
                  {fmt(subtotalAmountPaid)}
                </td>
                <td className={cn('px-2 py-2 text-right text-xs font-bold tabular-nums',
                  footerPossibleWinLoss < 0 ? 'text-red-600' : 'text-black')}>
                  {fmt(footerPossibleWinLoss)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function PreMatchSummary() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { matches, bets, isLoading } = useBetting();
  const { users: allUsers } = useUserManagement();

  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [expandedScores, setExpandedScores] = useState<Set<string>>(new Set());
  // Score simulation: 'NIL' = no simulation, otherwise the score being simulated as winning
  const [simulatedScore, setSimulatedScore] = useState<string>('NIL');
  // Match history view (shows only COMPLETED matches)
  const [showHistory, setShowHistory] = useState(false);

  // Fight settings keyed by settingKey(score, userId)
  const [savedSettings, setSavedSettings] = useState<Record<string, FightSetting>>({});
  const [drafts, setDrafts] = useState<Record<string, DraftState>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Only company/agent can edit fight settings
  const canEdit = !!user && (user.tier === UserTier.MASTER || user.tier === UserTier.COMPANY || user.tier === UserTier.AGENT);

  const toggleScore = (score: string) => {
    setExpandedScores(prev => {
      const next = new Set(prev);
      if (next.has(score)) next.delete(score); else next.add(score);
      return next;
    });
  };

  // Active matches (non-completed) shown in main list
  const upcomingMatches = useMemo(() =>
    matches.filter(m =>
      m.status === MatchStatus.UPCOMING ||
      m.status === MatchStatus.LIVE
    ), [matches]);

  // Completed matches shown in history view
  // Completed matches shown in history view — latest first
  const completedMatchesList = useMemo(() =>
    matches
      .filter(m =>
        m.status === MatchStatus.COMPLETED &&
        m.scoreOdds &&
        Object.keys(m.scoreOdds).length > 0
      )
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()),
  [matches]);

  // Visible user IDs (self + all descendants)
  const visibleUserIds = useMemo(() => {
    if (!user) return new Set<string>();
    return new Set<string>([user.id, ...getDescendantIds(user.id, allUsers)]);
  }, [user, allUsers]);

  // userId → ticketPrice
  const ticketPriceByUser = useMemo(() => {
    const map: Record<string, number> = {};
    for (const u of allUsers) map[u.id] = u.ticketPrice ?? 1;
    if (user) map[user.id] = user.ticketPrice ?? 1;
    return map;
  }, [allUsers, user]);

  // userId → username
  const usernameByUser = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of allUsers) map[u.id] = u.username;
    if (user) map[user.id] = user.username;
    return map;
  }, [allUsers, user]);

  // Aggregate bets by score for the selected match
  const summaryByScore = useMemo((): Record<string, ScoreData> => {
    if (!selectedMatch) return {};
    const result: Record<string, ScoreData> = {};
    for (const score of Object.keys(selectedMatch.scoreOdds ?? {})) {
      result[score] = { totalAfterRate: 0, totalBetsPlaced: 0, totalDownlineRebate: 0, perUser: {} };
    }
    const matchBets = bets.filter(b =>
      b.matchId === selectedMatch.id &&
      b.status !== BetStatus.CANCELLED &&
      visibleUserIds.has(b.userId)
    );
    for (const bet of matchBets) {
      // Normalise 'Any Other Score' → 'AOS' to match scoreOdds key
      const score = bet.selection === 'Any Other Score' ? 'AOS' : bet.selection;
      if (!result[score]) result[score] = { totalAfterRate: 0, totalBetsPlaced: 0, totalDownlineRebate: 0, perUser: {} };
      const tp = ticketPriceByUser[bet.userId] ?? 1;
      const afterRate = bet.amount * tp;
      result[score].totalAfterRate += afterRate;
      result[score].totalBetsPlaced += bet.amount;
      // Track each bettor's rebate paid out: bet amount × (1 - their own ticketPrice)
      result[score].totalDownlineRebate += bet.amount * (1 - tp);
      if (!result[score].perUser[bet.userId]) {
        result[score].perUser[bet.userId] = {
          tickets: 0, ticketPrice: tp, afterRate: 0,
          username: usernameByUser[bet.userId] ?? bet.userId,
        };
      }
      result[score].perUser[bet.userId].tickets += bet.amount;
      result[score].perUser[bet.userId].afterRate += afterRate;
    }
    return result;
  }, [selectedMatch, bets, visibleUserIds, ticketPriceByUser, usernameByUser]);

  // Split valid scores into home / draw+AOS / away
  const { homeScores, drawScores, awayScores } = useMemo(() => {
    if (!selectedMatch?.scoreOdds) return { homeScores: [], drawScores: [], awayScores: [] };
    const valid = Object.keys(selectedMatch.scoreOdds).filter(k => {
      const normKey = k.toUpperCase() === 'AOS' || k === 'Any Other Score' ? 'AOS' : k;
      // AOS is always included (odds may be 0 as placeholder); exclude 5-5
      if (normKey === 'AOS') return true;
      return (selectedMatch.scoreOdds![k] ?? 0) > 0 && k !== '5-5';
    // Normalise to AOS key
    }).map(k => k === 'Any Other Score' ? 'AOS' : k);
    const home: string[] = [], draw: string[] = [], away: string[] = [];
    for (const k of valid) {
      const cat = classifyScore(k);
      if (cat === 'home') home.push(k);
      else if (cat === 'draw' || cat === 'aos') draw.push(k);
      else away.push(k);
    }
    return { homeScores: sortScoreKeys(home), drawScores: sortScoreKeys(draw), awayScores: sortScoreKeys(away) };
  }, [selectedMatch]);

  // Grand total — computed over the same score set as the table (positive-odds scores only)
  const grandTotal = useMemo(() => {
    const tableScores = [...homeScores, ...drawScores, ...awayScores];
    return tableScores.reduce((s, sc) => s + (summaryByScore[sc]?.totalBetsPlaced ?? 0), 0);
  }, [summaryByScore, homeScores, drawScores, awayScores]);

  // scoreOdds convenience
  const scoreOdds = useMemo(() =>
    selectedMatch?.scoreOdds
      ? Object.fromEntries(Object.entries(selectedMatch.scoreOdds).map(([k, v]) => [k, v ?? 0]))
      : {}, [selectedMatch]);

  // Fight % options for the current user: 0, 5, 10, ... up to fightPercentage
  const fightPctOptions = useMemo(() => {
    const max = user?.fightPercentage ?? 0;
    const opts: number[] = [];
    for (let p = 0; p <= max; p += 5) opts.push(p);
    if (!opts.includes(0)) opts.unshift(0);
    return opts;
  }, [user]);

  // Load saved settings from DB when a match is selected
  // Settings are stored per downline-userId + score pair
  const loadSettings = useCallback(async (matchId: string, userIdSet: Set<string>) => {
    if (!user) return;
    setSettingsLoading(true);
    try {
      const ids = Array.from(userIdSet);
      const { data: json, error } = await supabase.functions.invoke('fight_settings_2026_05_06', {
        body: { action: 'get', userIds: ids, matchId },
      });
      if (error) throw error;
      if (json?.success && json?.data) {
        const dbSettings: Record<string, FightSetting> = {};
        const initDrafts: Record<string, DraftState> = {};
        if (json.multiUser) {
          // data: { [userId]: { [score]: { fightTicket, fightPct } } }
          for (const [uid, scoreMap] of Object.entries(json.data as Record<string, Record<string, FightSetting>>)) {
            for (const [score, val] of Object.entries(scoreMap)) {
              const k = settingKey(score, uid);
              dbSettings[k] = val;
              initDrafts[k] = { fightTicket: val.fightTicket.toString(), fightPct: val.fightPct, confirming: false };
            }
          }
        } else {
          // Single-user flat: { [score]: { fightTicket, fightPct } }
          for (const [score, val] of Object.entries(json.data as Record<string, FightSetting>)) {
            const k = settingKey(score, ids[0]);
            dbSettings[k] = val;
            initDrafts[k] = { fightTicket: (val as FightSetting).fightTicket.toString(), fightPct: (val as FightSetting).fightPct, confirming: false };
          }
        }
        setSavedSettings(dbSettings);
        setDrafts(initDrafts);
      }
    } catch (e) {
      console.error('loadSettings error', e);
    } finally {
      setSettingsLoading(false);
    }
  }, [user]);

  // When a match is selected, reset state and load settings for all visible users
  useEffect(() => {
    if (!selectedMatch) {
      setSavedSettings({});
      setDrafts({});
      setSimulatedScore('NIL');
      return;
    }
    setDrafts({});
    setSavedSettings({});
    setSimulatedScore('NIL');
    loadSettings(selectedMatch.id, visibleUserIds);
  }, [selectedMatch?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get or init draft for a key
  const getDraft = (key: string): DraftState => {
    if (drafts[key]) return drafts[key];
    const saved = savedSettings[key];
    if (saved) return { fightTicket: saved.fightTicket.toString(), fightPct: saved.fightPct, confirming: false };
    return { fightTicket: '0', fightPct: 0, confirming: false };
  };

  // Draft change handler
  const handleDraftChange = (key: string, d: Partial<DraftState>) => {
    setDrafts(prev => ({
      ...prev,
      [key]: { ...getDraft(key), ...d },
    }));
  };

  // Clicking Save → show confirmation
  const handleSaveClick = (key: string) => {
    setDrafts(prev => ({
      ...prev,
      [key]: { ...getDraft(key), confirming: true },
    }));
  };

  // Confirm YES → persist to DB
  // Key format: score__userId — score is everything before __, userId is everything after
  const handleConfirmYes = async (key: string) => {
    if (!user || !selectedMatch) return;
    const draft = getDraft(key);
    const ticketNum = parseFloat(draft.fightTicket);
    const fightTicket = isNaN(ticketNum) ? 0 : ticketNum;
    // Parse score and target userId from composite key
    const separatorIdx = key.indexOf('__');
    const score = key.substring(0, separatorIdx);
    const targetUserId = key.substring(separatorIdx + 2);
    setSavingKey(key);
    try {
      const { data: json, error } = await supabase.functions.invoke('fight_settings_2026_05_06', {
        body: {
          action: 'save',
          userId: targetUserId,  // save per the downline user
          matchId: selectedMatch.id,
          score,
          fightTicket,
          fightPct: draft.fightPct,
        },
      });
      if (!error && json?.success) {
        setSavedSettings(prev => ({
          ...prev,
          [key]: { fightTicket, fightPct: draft.fightPct },
        }));
        setDrafts(prev => ({
          ...prev,
          [key]: { ...draft, fightTicket: fightTicket.toString(), confirming: false },
        }));
      } else {
        console.error('save failed', error ?? json);
      }
    } catch (e) {
      console.error('save error', e);
    } finally {
      setSavingKey(null);
    }
  };

  // Confirm NO → revert draft to last saved
  const handleConfirmNo = (key: string) => {
    const s = savedSettings[key] ?? { fightTicket: 0, fightPct: 0 };
    setDrafts(prev => ({
      ...prev,
      [key]: { fightTicket: s.fightTicket.toString(), fightPct: s.fightPct, confirming: false },
    }));
  };


  // ── Match list view ───────────────────────────────────────────────────────
  const renderMatchCards = (matchList: typeof upcomingMatches, isHistoryView: boolean) => (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {matchList.map(match => {
        const start = new Date(match.startTime);
        const isClosed = !isHistoryView && new Date() >= new Date(start.getTime() - 5 * 60 * 1000);
        const oddsCount = Object.keys(match.scoreOdds ?? {}).filter(k => (match.scoreOdds![k] ?? 0) > 0).length;
        return (
          <Card
            key={match.id}
            className={cn('cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]', isClosed && 'opacity-60')}
            onClick={() => { setSelectedMatch(match); setExpandedScores(new Set()); }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-sm md:text-base flex items-center gap-2">
                  <BarChart2 className="h-4 w-4 text-primary shrink-0" />
                  {match.homeTeam} vs {match.awayTeam}
                </CardTitle>
                <Badge
                  variant={isHistoryView ? 'outline' : isClosed ? 'destructive' : 'secondary'}
                  className={cn('shrink-0', isHistoryView && 'border-green-500 text-green-700')}
                >
                  {isHistoryView ? `✓ ${match.result ?? 'Completed'}` : isClosed ? 'Closed' : match.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground space-y-1">
              <p>{format(toMYT(new Date(match.startTime)), 'PPp')} MYT</p>
              <p>{oddsCount} odds available</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const matchListView = (
    <div className="space-y-6 p-4 md:p-6 pt-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-sm">{t.preMatch.pageSubtitle}</p>
        <Button
          variant="outline" size="sm"
          onClick={() => setShowHistory(true)}
          className="h-8 text-xs px-3 gap-1.5 border border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30 shrink-0"
        >
          🏆 Match History ({completedMatchesList.length})
        </Button>
      </div>
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">{t.common.loading}</div>
      ) : upcomingMatches.length === 0 ? (
        <div className="text-center py-12">
          <div className="mb-4 text-4xl">⚽</div>
          <p className="text-muted-foreground">{t.preMatch.noMatches}</p>
        </div>
      ) : (
        renderMatchCards(upcomingMatches, false)
      )}
    </div>
  );

  // ── History view ──────────────────────────────────────────────────────────
  const historyView = (
    <div className="space-y-6 p-4 md:p-6 pt-4">
      <div className="flex items-center gap-3">
        <Button
          variant="outline" size="sm"
          onClick={() => setShowHistory(false)}
          className="h-7 text-[10px] px-2 gap-1 rounded bg-[rgb(125,225,129)] border border-input hover:bg-accent hover:text-accent-foreground shrink-0"
        >
          <ArrowLeft className="h-3 w-3" />
          {t.preMatch.backToMatches}
        </Button>
        <div>
          <h2 className="text-sm font-bold">🏆 Match History</h2>
          <p className="text-[10px] text-muted-foreground">{completedMatchesList.length} completed match{completedMatchesList.length !== 1 ? 'es' : ''}</p>
        </div>
      </div>
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">{t.common.loading}</div>
      ) : completedMatchesList.length === 0 ? (
        <div className="text-center py-12">
          <div className="mb-4 text-4xl">🏆</div>
          <p className="text-muted-foreground">No completed matches yet.</p>
        </div>
      ) : (
        renderMatchCards(completedMatchesList, true)
      )}
    </div>
  );

  // ── Detail view ───────────────────────────────────────────────────────────
  const detailView = selectedMatch && (
    <div className="space-y-4 p-4 md:p-6 pt-4">
      {/* Back + title */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline" size="sm"
          onClick={() => setSelectedMatch(null)}
          className="h-6 text-[10px] px-1.5 gap-1 rounded bg-[rgb(125,225,129)] border border-input hover:bg-accent hover:text-accent-foreground shrink-0"
        >
          <ArrowLeft className="h-3 w-3" />
          {t.preMatch.backToMatches}
        </Button>
        <div>
          <h2 className="text-xs md:text-xl font-bold tracking-tight">
            {selectedMatch.homeTeam} vs {selectedMatch.awayTeam}
          </h2>
          <p className="text-[10px] text-muted-foreground">
            {format(toMYT(selectedMatch.startTime), 'PPpp')} MYT
          </p>
        </div>
      </div>

      {/* Grand total */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-3 flex items-center gap-3">
          <TrendingUp className="h-5 w-5 text-primary shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Total Bets Placed (All Scores)</p>
            <p className="text-lg font-bold text-primary">{grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
        </CardContent>
      </Card>

      {settingsLoading && (
        <p className="text-xs text-muted-foreground text-center">Loading fight settings…</p>
      )}

      {/* Score Simulation dropdown + Combined scores table */}
      <div className="space-y-3">
        {/* Score Simulation control */}
        <div className="flex items-center gap-3 px-1">
          <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Score Simulation:</span>
          <div className="relative">
            <select
              value={simulatedScore}
              onChange={e => setSimulatedScore(e.target.value)}
              className="text-xs border rounded px-2 py-1.5 pr-7 bg-background hover:bg-muted focus:outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer min-w-[110px] font-medium"
            >
              <option value="NIL">NIL</option>
              {[...homeScores, ...drawScores, ...awayScores].map(sc => (
                <option key={sc} value={sc}>{sc}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          </div>
          {simulatedScore !== 'NIL' && (
            <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded px-2 py-1">
              <span className="font-semibold">★ Simulating: {simulatedScore} wins</span>
              <span className="text-muted-foreground">— Focus for other scores set to 0</span>
            </div>
          )}
        </div>

        <CombinedScoreTable
          homeScores={homeScores}
          drawScores={drawScores}
          awayScores={awayScores}
          summaryByScore={summaryByScore}
          scoreOdds={scoreOdds}
          tPreMatch={t.preMatch}
          viewerTicketPrice={user?.ticketPrice ?? 1}
          simulatedScore={simulatedScore}
          isCompany={user?.tier === UserTier.COMPANY || user?.tier === UserTier.MASTER}
        />
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-background text-[13px] lg:text-base">
        <div className="border-b bg-card px-4 md:px-6 py-4">
          <div className="flex items-center gap-3">
            <BarChart2 className="h-5 w-5 text-primary" />
            <div>
              <h1 className="text-lg font-bold">{t.preMatch.pageTitle}</h1>
              {!selectedMatch && !showHistory && (
                <p className="text-xs text-muted-foreground">{t.preMatch.selectMatch}</p>
              )}
              {showHistory && !selectedMatch && (
                <p className="text-xs text-muted-foreground">Viewing completed matches</p>
              )}
            </div>
          </div>
        </div>
        {selectedMatch ? detailView : showHistory ? historyView : matchListView}
      </div>
    </Layout>
  );
}